// R58-L-03 — per-user review progress bar.
//
// Renders a thin `<LinearProgress variant="determinate">` with the
// proper ARIA attributes (role=progressbar, aria-valuemin/now/max,
// aria-label) and a "K / N (X %)" caption per R58-L-03 AC1. The 1 s
// invalidation latency (AC2) is delivered by L-02's upsert hook, which
// invalidates this same TanStack key on every successful image visit.
//
// Hidden when the DA's evaluationState is not in a reviewable phase
// (NOT_STARTED / LOCKED / REJECTED) — see Story 065 §"phase has no
// progress bar".
//
// Story 065 history: shipped a self-keyed `useReviewProgressQuery`
// rooted at `[...analysisKeys.detail(daId), "review-progress"]`. R58-L-03
// migrates this consumer to the L-02-contracted key
// `["review-progress", daId, "self"]` so the L-02 upsert invalidation
// (useImageReviewState.ts:88) drives the bar live. The legacy hook
// `useReviewProgressQuery` is preserved for WorkflowHeaderActions — it
// reads the same endpoint, just under a different cache key — until
// that consumer also migrates.
//
// Contract pin: lyse/v1@5.8.0 (no contract change for L-03).
import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import { useDataPointsQuery } from "../queries.js";
import { defaultSelection } from "../state/dataPointFilterStore.js";

// SB3-CC-G (lyse/v1@6.0.0): unified single-axis 8-value vocabulary.
type DataAcquisitionState =
  | "CREATED"
  | "PROCESSING"
  | "PRE_SCREENING"
  | "EXPERT_ANALYSIS"
  | "EVALUATION_DONE"
  | "LOCKED"
  | "ISSUE"
  | "REJECTED";

export interface ReviewProgressBarProps {
  dataAcquisitionId: string;
  evaluationState: DataAcquisitionState | null;
}

const REVIEWABLE: ReadonlySet<DataAcquisitionState> = new Set([
  "PRE_SCREENING",
  "EXPERT_ANALYSIS",
  "EVALUATION_DONE",
]);

/**
 * Wire shape (per `/review-progress` BR-093). Documented here so the
 * component owns its only-consumer DTO without a packages/types hop.
 */
interface SelfReviewProgressResponse {
  phase: "PRE_SCREENING" | "EXPERT_ANALYSIS" | "EVALUATION_DONE" | null;
  viewedCount: number;
  total: number;
  viewedDefectIds?: string[];
}

/**
 * R58-L-03 hook. Keyed on `["review-progress", daId, "self"]` —
 * EXACT shape contracted with L-02's useImageReviewState upsert
 * (useImageReviewState.ts:88,94 + useImageReviewState.test.tsx:220).
 * Changing this key drops the live progress increment described in
 * R58-L-03 AC2 — keep the literal tuple in sync if you ever need to
 * refactor.
 */
export function useSelfReviewProgress(dataAcquisitionId: string) {
  const api = useApi();
  return useQuery<SelfReviewProgressResponse>({
    queryKey: ["review-progress", dataAcquisitionId, "self"],
    enabled: dataAcquisitionId.length > 0,
    queryFn: async () => {
      const response = await api.get<SelfReviewProgressResponse>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/review-progress`,
      );
      return response.data;
    },
    // staleTime: 0 — the L-02 upsert invalidates this key after every
    // successful image visit (debounce 300 ms). A fresh fetch each
    // invalidation gives the analyst sub-second progress feedback.
    staleTime: 0,
    refetchOnWindowFocus: true,
    // Suppress 404 retries — the dev-api stub may not be restarted yet
    // and a chain of re-renders during interaction can stall RGL drag
    // gestures (Story 010 AC3 regression).
    retry: false,
  });
}

/**
 * Pure helper. Rounds `viewed/total` to the nearest integer percent;
 * clamps to [0, 100]; defends against NaN / non-finite inputs by
 * returning 0. Tests pin all the edge cases.
 */
export function computeReviewPercent(viewed: number, total: number): number {
  if (!Number.isFinite(viewed) || !Number.isFinite(total)) return 0;
  if (total <= 0) return 0;
  const raw = Math.round((viewed / total) * 100);
  if (raw < 0) return 0;
  if (raw > 100) return 100;
  return raw;
}

export function ReviewProgressBar({
  dataAcquisitionId,
  evaluationState,
}: ReviewProgressBarProps): React.ReactElement | null {
  const reviewable = evaluationState ? REVIEWABLE.has(evaluationState) : false;
  // Only fire the query when the bar would be visible — Story 010 AC3's
  // RGL drag depends on a stable render tree during the gesture; a
  // 404'ing useQuery on a LOCKED detail stub triggered a re-render
  // mid-drag.
  const progress = useSelfReviewProgress(reviewable ? dataAcquisitionId : "");
  // SB6-ANA-FILT-05 (D11) — the denominator is the count of ACTIVE data
  // points, not the all-status total the review-progress endpoint
  // historically returned (e.g. 8 / 319, not 8 / 321). We derive the
  // ACTIVE total from a tiny data-points query (pageSize 1; only `total`
  // is read). Falls back to the review-progress `total` when the
  // data-points query hasn't landed yet.
  const activeTotalQuery = useDataPointsQuery(
    reviewable ? dataAcquisitionId : "",
    { ...defaultSelection(), status: ["ACTIVE"] },
    { pageSize: 1, enabled: reviewable },
  );
  if (!reviewable) return null;
  const activeTotal = activeTotalQuery.data?.total;
  const total = activeTotal ?? progress.data?.total ?? 0;
  const viewed = progress.data?.viewedCount ?? 0;
  // For EVALUATION_DONE the denominator is frozen (no further review
  // happens); render 100% so the visual matches "this phase is done".
  const percent =
    evaluationState === "EVALUATION_DONE"
      ? 100
      : computeReviewPercent(viewed, total);

  // R58-L-03 AC3 — the disabled-state tooltip names the count of unseen
  // images. We surface that count next to the bar so the analyst knows
  // exactly how many visits remain before the gate enables.
  const unseen = Math.max(total - viewed, 0);
  const tooltipText =
    evaluationState === "EVALUATION_DONE"
      ? `${String(total)} defects frozen for sign-off`
      : percent >= 100
        ? "All images reviewed"
        : `${String(unseen)} image${unseen === 1 ? "" : "s"} still unseen`;

  // R58-L-03 AC1 — "K / N (X %)" format. The previous Story 065 caption
  // "K / N viewed (X%)" is updated here in lock-step with the AC.
  const captionText =
    evaluationState === "EVALUATION_DONE"
      ? `${String(total)} defects · frozen for sign-off`
      : `${String(viewed)} / ${String(total)} (${String(percent)} %)`;

  return (
    <Box
      sx={{ width: "100%", display: "flex", alignItems: "center", gap: 1.5 }}
      data-testid="status-progress"
      role="region"
      aria-label="Review progress"
    >
      <Tooltip title={tooltipText} arrow>
        <LinearProgress
          variant="determinate"
          value={percent}
          role="progressbar"
          aria-label="Defect review progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-valuetext={`${String(viewed)} of ${String(total)} viewed (${String(percent)}%)`}
          sx={{ flex: 1, height: 8, borderRadius: 1 }}
        />
      </Tooltip>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{
          minWidth: 160,
          textAlign: "right",
          fontVariantNumeric: "tabular-nums",
        }}
        data-testid="review-progress-bar-label"
      >
        {captionText}
      </Typography>
    </Box>
  );
}
